/**
 * useModels Hook - Fetch available AI models
 */

import { useQuery } from '@tanstack/react-query'
import type { AIModel } from '@/components/chat/model-selector'

interface ModelsResponse {
  models: AIModel[]
  default: string | null
}

const STORAGE_KEY = 'nurin-selected-model'

/**
 * Fetch available AI models from API
 */
const API_BASE = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api/v1`
  : '/api/v1'

async function fetchModels(): Promise<ModelsResponse> {
  const res = await fetch(`${API_BASE}/chat/models`)
  if (!res.ok) {
    throw new Error('Failed to fetch models')
  }
  return res.json()
}

/**
 * Hook to get available models
 */
export function useModels() {
  return useQuery({
    queryKey: ['ai-models'],
    queryFn: fetchModels,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  })
}

/**
 * Get saved model preference from localStorage
 */
export function getSavedModel(): string | null {
  if (typeof window === 'undefined') return null
  return localStorage.getItem(STORAGE_KEY)
}

/**
 * Save model preference to localStorage
 */
export function saveSelectedModel(modelId: string): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(STORAGE_KEY, modelId)
}

/**
 * Hook to manage selected model with persistence
 */
export function useSelectedModel(defaultModel: string | null) {
  // Get initial value from localStorage or use default
  const getInitialModel = () => {
    const saved = getSavedModel()
    return saved || defaultModel || null
  }

  return {
    getInitialModel,
    saveModel: saveSelectedModel,
  }
}
